import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { payoffPlanService } from '../../../services/payoffPlanService';
import { formatCurrency } from '../../../utils/currency';

const SavedPlansManager = ({ debt, onClose, selectedDebts = [] }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [filterStrategy, setFilterStrategy] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');

  const isMultiDebt = selectedDebts?.length > 0;

  useEffect(() => {
    loadPlans();
  }, [debt?.id, selectedDebts]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      setError('');
      
      let plansData;
      if (isMultiDebt) {
        plansData = await payoffPlanService?.getAllForMultipleDebts(selectedDebts?.map(d => d?.id));
      } else {
        plansData = await payoffPlanService?.getAllForDebt(debt?.id);
      }
      
      setPlans(plansData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;

    try {
      await payoffPlanService?.delete(planId);
      await loadPlans();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleToggleSelection = (planId) => {
    setSelectedPlans(prev => {
      if (prev?.includes(planId)) {
        return prev?.filter(id => id !== planId);
      } else {
        return [...prev, planId];
      }
    });
  };

  const getStrategyLabel = (strategy) => {
    const labels = {
      avalanche: 'Avalanche (Highest Interest)',
      snowball: 'Snowball (Smallest Balance)',
      hybrid: 'Hybrid',
      custom: 'Custom',
      focused: 'Focused Debt',
      current: 'Current Plan'
    };
    return labels?.[strategy] || strategy;
  };

  const getStrategyColor = (strategy) => {
    const colors = {
      avalanche: 'var(--color-primary)',
      snowball: 'var(--color-success)',
      hybrid: 'var(--color-warning)',
      custom: 'var(--color-muted-foreground)',
      focused: 'var(--color-destructive)',
      current: 'var(--color-muted-foreground)'
    };
    return colors?.[strategy] || 'var(--color-muted-foreground)';
  };

  const calculateOriginalPlan = () => {
    if (isMultiDebt || !debt?.startDate || !debt?.endDate || !debt?.termLength) return null;

    const monthlyRate = debt?.interestRate / 100 / 12;
    const totalInterest = debt?.principalAmount * monthlyRate * debt?.termLength;

    return {
      planName: 'Original Debt Terms',
      strategy: 'current',
      monthsToPayoff: debt?.termLength,
      totalInterest: totalInterest,
      projectedPayoffDate: debt?.endDate,
      isOriginal: true
    };
  };

  const getFilteredPlans = () => {
    let filtered = plans;

    if (filterStrategy !== 'all') {
      filtered = filtered?.filter(p => p?.strategy === filterStrategy);
    }

    if (filterDateRange !== 'all') {
      const now = new Date();
      filtered = filtered?.filter(p => {
        if (!p?.projectedPayoffDate) return false;
        const payoffDate = new Date(p?.projectedPayoffDate);
        const monthsDiff = (payoffDate - now) / (1000 * 60 * 60 * 24 * 30);
        
        if (filterDateRange === '12months') return monthsDiff <= 12;
        if (filterDateRange === '24months') return monthsDiff <= 24;
        if (filterDateRange === '36months') return monthsDiff <= 36;
        return true;
      });
    }

    return filtered;
  };

  const originalPlan = calculateOriginalPlan();
  const filteredPlans = getFilteredPlans();
  const allPlans = originalPlan ? [originalPlan, ...filteredPlans] : filteredPlans;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
        <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-5xl p-24">
          <div className="flex items-center justify-center py-48">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-24 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-xl font-semibold text-card-foreground">Saved Payoff Plans</h2>
            <p className="text-sm text-muted-foreground mt-4">
              {isMultiDebt ? `${selectedDebts?.length} debts selected` : debt?.debtName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-8 hover:bg-muted rounded-lg transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-24">
          {error && (
            <div className="mb-16 p-12 bg-destructive/10 border border-destructive rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Filters */}
          <div className="mb-20 p-16 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-8 mb-12">
              <Icon name="Filter" size={16} color="var(--color-muted-foreground)" />
              <span className="text-sm font-semibold text-foreground">Filters</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <label className="block text-xs text-muted-foreground mb-6">Strategy</label>
                <select
                  value={filterStrategy}
                  onChange={(e) => setFilterStrategy(e?.target?.value)}
                  className="w-full px-12 py-8 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Strategies</option>
                  <option value="avalanche">Avalanche</option>
                  <option value="snowball">Snowball</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="custom">Custom</option>
                  <option value="focused">Focused</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-6">Payoff Date</label>
                <select
                  value={filterDateRange}
                  onChange={(e) => setFilterDateRange(e?.target?.value)}
                  className="w-full px-12 py-8 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Dates</option>
                  <option value="12months">Within 12 months</option>
                  <option value="24months">Within 24 months</option>
                  <option value="36months">Within 36 months</option>
                </select>
              </div>
            </div>
          </div>

          {allPlans?.length === 0 ? (
            <div className="text-center py-48">
              <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-16" />
              <p className="text-muted-foreground mb-8">No saved plans yet</p>
              <p className="text-sm text-muted-foreground">Create plans in the What-If Analysis section</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-16">
                <p className="text-sm text-muted-foreground">
                  {allPlans?.length} plan{allPlans?.length !== 1 ? 's' : ''} {filterStrategy !== 'all' || filterDateRange !== 'all' ? '(filtered)' : ''}
                </p>
                {plans?.length > 1 && (
                  <Button
                    onClick={() => setComparisonMode(!comparisonMode)}
                    size="sm"
                    className="bg-muted text-foreground hover:bg-muted/80"
                  >
                    <Icon name="BarChart2" size={16} className="mr-8" />
                    {comparisonMode ? 'Exit Comparison' : 'Compare Plans'}
                  </Button>
                )}
              </div>

              <div className="space-y-12">
                {allPlans?.map((plan) => {
                  const isSelected = selectedPlans?.includes(plan?.id);
                  const isOriginal = plan?.isOriginal;

                  return (
                    <div
                      key={plan?.id || 'original'}
                      className={`bg-muted/50 rounded-lg p-20 border transition-smooth ${
                        isSelected ? 'border-primary bg-primary/5' : 'border-border'
                      } ${comparisonMode && !isOriginal ? 'cursor-pointer hover:border-primary/50' : ''}`}
                      onClick={() => comparisonMode && !isOriginal && handleToggleSelection(plan?.id)}
                    >
                      <div className="flex items-start justify-between mb-12">
                        <div className="flex items-center gap-12">
                          {comparisonMode && !isOriginal && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSelection(plan?.id)}
                              className="w-20 h-20 rounded border-border"
                            />
                          )}
                          <div>
                            <div className="flex items-center gap-8 mb-4">
                              <h3 className="font-semibold text-foreground">{plan?.planName}</h3>
                              {isOriginal && (
                                <span className="text-xs px-8 py-2 rounded-full bg-muted text-muted-foreground">
                                  Original
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-8">
                              <span
                                className="text-xs px-8 py-2 rounded-full"
                                style={{
                                  backgroundColor: `${getStrategyColor(plan?.strategy)}20`,
                                  color: getStrategyColor(plan?.strategy)
                                }}
                              >
                                {getStrategyLabel(plan?.strategy)}
                              </span>
                            </div>
                          </div>
                        </div>
                        {!isOriginal && (
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleDeletePlan(plan?.id);
                            }}
                            className="p-8 hover:bg-destructive/10 rounded-lg transition-smooth"
                            aria-label="Delete plan"
                          >
                            <Icon name="Trash2" size={16} color="var(--color-destructive)" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-16">
                        <div>
                          <p className="text-xs text-muted-foreground mb-4">Payoff Time</p>
                          <p className="text-lg font-semibold text-foreground">
                            {plan?.monthsToPayoff} months
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-4">Total Interest</p>
                          <p className="text-lg font-semibold text-foreground">
                            {formatCurrency(plan?.totalInterest)}
                          </p>
                        </div>
                        {!isOriginal && plan?.interestSaved > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-4">Interest Saved</p>
                            <p className="text-lg font-semibold text-success">
                              {formatCurrency(plan?.interestSaved)}
                            </p>
                          </div>
                        )}
                        {!isOriginal && plan?.timeSavedMonths > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-4">Time Saved</p>
                            <p className="text-lg font-semibold text-success">
                              {plan?.timeSavedMonths} months
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-muted-foreground mb-4">Payoff Date</p>
                          <p className="text-sm font-semibold text-foreground">
                            {plan?.projectedPayoffDate ? new Date(plan?.projectedPayoffDate)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedPlansManager;